import { PhoneIcon } from 'assets/icons';
import Button from 'components/button';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import FieldLabel from 'components/styledInput/FieldLabel';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import StyledInput from 'components/styledInput/StyledInput';
import AppointmentForm from 'components/planning/AppointmentForm';
import ChoiceChips from 'components/tasks/ChoiceChips';
import { COMPANY_DELETE_REASONS } from 'constants/companyDeleteReasons';
import { fr } from 'date-fns/locale';
import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import useText from 'hooks/useText';
import { useEffect, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

registerLocale('fr-FR', fr);

const CALL_RESULTS = ['answered', 'callback', 'no_answer', 'not_interested'];
const LOST_REASONS = ['price', 'competitor', 'noNeed', 'direct', 'unreachable', 'closed'];
const isMail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// What a form holds when it opens: the contact it was opened for (or the
// first one), a follow-up in three months.
const initialForm = (data, now = new Date()) => {
  const contacts = data.contacts ?? [];
  const contact = contacts.find((one) => one.name === data.contactName) ?? contacts[0] ?? null;
  return {
    body: '',
    contactName: contact?.name ?? '',
    result: 'answered',
    note: '',
    reason: 'price',
    details: '',
    deleteReason: '',
    groupName: data.recordName ?? '',
    relaunch: isoDay(addDays(now, 90)),
    noRelaunch: false,
    firstname: data.contact?.firstname ?? '',
    lastname: data.contact?.lastname ?? '',
    position: data.contact?.position ?? '',
    phone: data.contact?.phone ?? '',
    email: data.contact?.email ?? '',
    search: '',
  };
};

/**
 * The forms of a record page, in the slide-over (`objID`: { kind, companyId
 * | groupId, recordName, contacts, contactName, contact }): a note, a call
 * made, an appointment, a company lost, a contact of a group, a
 * company joining a group, a company made a group, a prospect deleted. Each writes through server/crm/crmApi and tells
 * the page (useRecordChanged).
 */
const RecordFormPanel = () => {
  const { objID } = UseContext();
  const data = objID && typeof objID === 'object' && objID.kind ? objID : null;
  const { text } = useText('recordForm');
  const { call } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const navigate = useNavigate();
  const notify = useNotifyRecordChanged();
  const [busy, setBusy] = useState(false);
  const contacts = data?.contacts ?? [];
  const only = contacts.length === 1 ? contacts[0] : null;
  const target = data?.groupId ? { groupId: data.groupId } : { companyId: data?.companyId };

  const [form, setForm] = useState({});
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  // A new form at every opening.
  useEffect(() => {
    if (objID && typeof objID === 'object' && objID.kind) setForm(initialForm(objID));
  }, [objID]);

  // The companies that may join the group, as the search narrows them.
  const [candidates, setCandidates] = useState(null);
  useEffect(() => {
    if (data?.kind !== 'groupCompany') return undefined;
    const timer = setTimeout(
      () => call(crmApi.listGroupCandidates, { search: form.search ?? '' }).then(setCandidates, () => setCandidates([])),
      250,
    );
    return () => clearTimeout(timer);
  }, [call, data?.kind, form.search]);

  if (!data) return null;
  const close = () => toggleSlideOver();
  // The result of `run`, or null when it failed (the error is toasted).
  const submit = async (run, message) => {
    setBusy(true);
    try {
      const result = await run();
      toast.success(message);
      notify();
      close();
      return result ?? true;
    } catch (error) {
      toast.error(error.message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const contactOptions = contacts.map((contact) => ({ value: contact.name, label: contact.name }));
  const contact = contacts.find((one) => one.name === form.contactName);

  const FORMS = {
    note: {
      title: text('noteTitle'),
      cta: text('noteCta'),
      valid: Boolean(form.body?.trim()),
      save: () => submit(() => call(crmApi.addRecordNote, { ...target, body: form.body, contactName: data.contactName ?? '' }), text('noteSaved')),
      body: (
        <>
          <StyledInput
            type="textarea"
            label={text('noteLabel')}
            placeholder={text('notePlaceholder')}
            value={form.body ?? ''}
            onChange={(event) => set('body', event.target.value)}
          />
        </>
      ),
    },
    call: {
      title: text('callTitle', { name: only?.name ?? data.recordName }),
      hint: only ? text('callHintOne', { company: data.recordName }) : text('callHintMany'),
      cta: text('callCta'),
      valid: true,
      save: () =>
        submit(() => call(crmApi.logRecordCall, { ...target, contactName: form.contactName, result: form.result, note: form.note }), text('callSaved')),
      body: (
        <>
          {contacts.length > 1 && (
            <StyledInput type="select" label={text('who')} list={contactOptions} value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          )}
          {contact?.phone && (
            <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:underline">
              <PhoneIcon wh={16} color="currentColor" />
              {contact.phone}
            </a>
          )}
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('resultLabel')} />
            <ChoiceChips options={CALL_RESULTS.map((result) => [result, text(`result_${result}`)])} value={form.result} onPick={(result) => set('result', result)} />
          </div>
          <StyledInput type="textarea" label={text('callNote')} placeholder={text('callNotePlaceholder')} value={form.note ?? ''} onChange={(event) => set('note', event.target.value)} />
        </>
      ),
    },
    // The company leaves the list: back to the list, where it is gone.
    delete: {
      title: text('deleteTitle'),
      cta: text('deleteCta'),
      valid: Boolean(form.deleteReason) && (form.deleteReason !== 'other' || Boolean(form.details?.trim())),
      save: () =>
        submit(
          () =>
            call(crmApi.deleteCompany, {
              companyId: data.companyId,
              reason: form.deleteReason,
              details: form.details ?? '',
            }),
          text('deleteSaved'),
        ).then((done) => done && navigate('/company')),
      body: (
        <>
          <p className="text-sm text-gray-700">{text('deleteWarning')}</p>
          <StyledInput
            type="select"
            label={text('deleteReason')}
            list={[
              { value: '', label: text('deleteReasonPlaceholder') },
              ...COMPANY_DELETE_REASONS.map((reason) => ({ value: reason, label: text(`deleteReason_${reason}`) })),
            ]}
            value={form.deleteReason}
            onChange={(event) => set('deleteReason', event.target.value)}
          />
          <StyledInput
            type="textarea"
            label={text(form.deleteReason === 'other' ? 'deleteDetailsRequired' : 'reasonDetails')}
            value={form.details ?? ''}
            onChange={(event) => set('details', event.target.value)}
          />
        </>
      ),
    },
    // A group of the company's name, the company in it: its page opens.
    toGroup: {
      title: text('toGroupTitle'),
      cta: text('toGroupCta'),
      valid: Boolean(form.groupName?.trim()),
      save: () =>
        submit(
          () => call(crmApi.makeCompanyGroup, { companyId: data.companyId, name: form.groupName.trim() }),
          text('toGroupSaved'),
        ).then((result) => result?.groupId && navigate(`/company/group/${result.groupId}`)),
      body: (
        <>
          <StyledInput
            type="text"
            label={text('groupName')}
            value={form.groupName ?? ''}
            onChange={(event) => set('groupName', event.target.value)}
          />
          <p className="text-sm text-gray-600">{text('toGroupHint')}</p>
        </>
      ),
    },
    lost: {
      title: text('lostTitle'),
      cta: text('lostCta'),
      valid: true,
      save: () =>
        submit(
          () =>
            call(crmApi.setCompanyStage, {
              companyId: data.companyId,
              stage: 'lost',
              lostReason: [text(`reason_${form.reason}`), form.details?.trim()].filter(Boolean).join(' · '),
              relaunchOn: form.noRelaunch ? null : form.relaunch,
            }),
          text('lostSaved'),
        ),
      body: (
        <>
          <StyledInput
            type="select"
            label={text('reason')}
            list={LOST_REASONS.map((reason) => ({ value: reason, label: text(`reason_${reason}`) }))}
            value={form.reason}
            onChange={(event) => set('reason', event.target.value)}
          />
          <StyledInput type="textarea" label={text('reasonDetails')} value={form.details ?? ''} onChange={(event) => set('details', event.target.value)} />
          {!form.noRelaunch && (
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={form.relaunch ? fromIsoDay(form.relaunch) : null}
              onChange={(date) => date && set('relaunch', isoDay(date))}
              locale="fr-FR"
              popperPlacement="bottom"
              customInput={<StyledDateInput label={text('relaunch')} />}
            />
          )}
          <StyledInput type="checkbox" label={text('noRelaunch')} checked={Boolean(form.noRelaunch)} onChange={() => set('noRelaunch', !form.noRelaunch)} />
        </>
      ),
    },
    groupContact: {
      title: text(data.contact ? 'contactEditTitle' : 'contactAddTitle'),
      cta: text('contactCta'),
      valid: Boolean(form.lastname?.trim()) && (!form.email?.trim() || isMail(form.email)),
      save: () =>
        submit(
          () =>
            call(crmApi.saveGroupContact, {
              groupId: data.groupId,
              contact: {
                ...data.contact,
                firstname: form.firstname.trim(),
                lastname: form.lastname.trim().toUpperCase(),
                position: form.position.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
              },
            }),
          text('contactSaved'),
        ),
      body: (
        <div className="grid grid-cols-2 gap-3">
          <StyledInput type="text" label={text('firstname')} value={form.firstname ?? ''} onChange={(event) => set('firstname', event.target.value)} />
          <StyledInput type="text" label={text('lastname')} value={form.lastname ?? ''} onChange={(event) => set('lastname', event.target.value)} />
          <StyledInput type="text" label={text('position')} value={form.position ?? ''} onChange={(event) => set('position', event.target.value)} />
          <StyledInput type="text" label={text('phone')} value={form.phone ?? ''} onChange={(event) => set('phone', event.target.value)} />
          <div className="col-span-2">
            <StyledInput type="email" label={text('email')} value={form.email ?? ''} onChange={(event) => set('email', event.target.value)} />
          </div>
        </div>
      ),
    },
    groupCompany: {
      title: text('groupCompanyTitle'),
      body: (
        <>
          <StyledInput type="text" label={text('searchCompany')} value={form.search ?? ''} onChange={(event) => set('search', event.target.value)} />
          <div className="flex flex-col">
            {(candidates ?? []).map((company) => (
              <div key={company.id} className="flex items-center gap-3 border-b border-gray-100 py-2.5 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium uppercase text-gray-900">{company.name}</p>
                  <p className="truncate text-xs text-gray-600">{company.department}</p>
                </div>
                <Button
                  color="SECONDARY"
                  size="SM"
                  disabled={busy}
                  onClick={() =>
                    submit(
                      () => call(crmApi.addCompanyToGroup, { companyId: company.id, groupId: data.groupId }),
                      text('groupCompanySaved', { company: company.name, group: data.recordName }),
                    )
                  }
                >
                  {text('groupCompanyCta')}
                </Button>
              </div>
            ))}
            {candidates && !candidates.length && <p className="py-4 text-sm text-gray-600">{text('groupCompanyNone')}</p>}
          </div>
        </>
      ),
    },
  };
  // An appointment: the one form of the planning, linked to the record, the
  // week it falls in beside it.
  if (data.kind === 'event') {
    const tomorrow = isoDay(addDays(new Date(), 1));
    return (
      <AppointmentForm
        key={JSON.stringify([data.companyId, data.groupId, data.contactName])}
        mode="create"
        layout="panel"
        record={{ companyId: data.companyId ?? null, name: data.recordName ?? '', contacts, contactName: data.contactName ?? '' }}
        initial={{ startDay: tomorrow, endDay: tomorrow, start: '10:00', end: '11:00' }}
        header={
          <SlideOverHeader hide={close} closeLabel={text('close')}>
            <span className="flex flex-col">
              {text('eventTitle')}
              <span className="text-sm font-normal text-gray-600">{data.recordName}</span>
            </span>
          </SlideOverHeader>
        }
        onSaved={() => {
          notify();
          close();
        }}
        onCancel={close}
      />
    );
  }
  const current = FORMS[data.kind];
  if (!current) return null;

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        <span className="flex flex-col">
          {current.title}
          <span className="text-sm font-normal text-gray-600">{current.hint ?? data.recordName}</span>
        </span>
      </SlideOverHeader>
      <SlideOverBody>
        <div className="flex flex-col gap-4">{current.body}</div>
      </SlideOverBody>
      {current.save && (
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            <Button color="SECONDARY" size="SM" onClick={close}>
              {text('cancel')}
            </Button>
            <Button color="PRIMARY" size="SM" disabled={!current.valid || busy} onClick={current.save}>
              {current.cta}
            </Button>
          </SlideOverFooter>
        </div>
      )}
    </>
  );
};

export default RecordFormPanel;
